import "@repo/guide-ui/message-bubble.css";

import {t} from "@lingui/core/macro";
import {StyleSheet} from "aphrodite";
import * as React from "react";

import Button from "@khanacademy/wonder-blocks-button";
import {View} from "@khanacademy/wonder-blocks-core";
import {Spring} from "@khanacademy/wonder-blocks-layout";
import Link from "@khanacademy/wonder-blocks-link";
import Tooltip from "@khanacademy/wonder-blocks-tooltip";
import {Body} from "@khanacademy/wonder-blocks-typography";

import useProfile from "~/data-access/use-profile.ts";

import type {guideTranscript_aiGuideThread_interactions as Interaction} from "./__graphql-types__/guideTranscript.ts";
import {canResumeActivity, getResumeUrl} from "./utils.ts";

interface Props {
    threadOwnerKaid: string;
    threadId: string;
    interactions: Array<Interaction>;
    componentKind?: "link" | "button";
    disabled?: boolean;
}

const MaybeResumeLink = ({
    threadOwnerKaid,
    threadId,
    interactions,
    componentKind = "link",
    disabled = false,
}: Props): React.ReactElement => {
    const profileResult = useProfile();
    // Hide resume link if the logged-in user does not own the thread
    if (
        profileResult.status !== "success" ||
        profileResult.profile?.get("kaid") !== threadOwnerKaid ||
        interactions.length === 0 ||
        !interactions[0].url
    ) {
        return <></>;
    }

    const firstInteractionURL = interactions[0].url;
    const pathname = new URL(firstInteractionURL).pathname;
    const canResume = canResumeActivity(pathname);
    let resumeURL = "";
    if (canResume) {
        resumeURL = getResumeUrl(firstInteractionURL, threadId);
    }

    return (
        <View style={styles.contextHeaderWrapper}>
            {/* Only display the resume chat link for activities where resuming chat is fully functional.
            Otherwise, display a message indicating resuming is not available. */}
            {canResume ? (
                <>
                    {resumeURL && (
                        <View>
                            <Spring />
                            {componentKind === "link" && (
                                <Link
                                    href={resumeURL}
                                    target="_blank"
                                    testId="restoreChat"
                                >
                                    {t`Restore this chat`}
                                </Link>
                            )}
                            {componentKind === "button" &&
                                (disabled ? (
                                    <Tooltip
                                        content={t`This thread cannot be resumed`}
                                    >
                                        <Button
                                            testId="restoreChat"
                                            kind="secondary"
                                            disabled={true}
                                        >
                                            {t`Resume chat`}
                                        </Button>
                                    </Tooltip>
                                ) : (
                                    <Button
                                        href={resumeURL}
                                        target="_blank"
                                        testId="restoreChat"
                                        kind="secondary"
                                    >
                                        {t`Resume chat`}
                                    </Button>
                                ))}
                            <Spring />
                        </View>
                    )}
                </>
            ) : (
                <View>
                    <Body>
                        {t`Restore chat is not currently supported for this activity - but we're working on it. Check back soon!`}
                    </Body>
                </View>
            )}
        </View>
    );
};

export default MaybeResumeLink;

const styles = StyleSheet.create({
    contextHeaderWrapper: {
        flexDirection: "row",
        minBlockSize: "auto",
    },
});
